/**
 * パフォーマンスヒートマップ
 * @description 7日×24時間の売上パターンを可視化
 */

import React, { useMemo, useState, useRef, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  Clock,
  TrendingUp,
  Info
} from 'lucide-react';
import { DataService } from '@/services/dataService';
import type { Reservation } from '@/types/reservation';
import type { TreatmentRecord } from '@/types/treatment';

interface HeatmapData {
  day: string;
  dayIndex: number;
  hour: number;
  revenue: number;
  intensity: number;
  customers: number;
}

interface HeatmapCellProps {
  data: HeatmapData;
  onClick: (data: HeatmapData) => void;
  width: number;
  height: number;
}

/**
 * ヒートマップセル
 */
const HeatmapCell: React.FC<HeatmapCellProps> = ({ data, onClick, width, height }) => {
  const getIntensityColor = (intensity: number): string => {
    if (intensity === 0) return 'bg-muted/30';
    if (intensity > 80) return 'bg-green-900';
    if (intensity > 60) return 'bg-green-600';
    if (intensity > 40) return 'bg-lime-500';
    return 'bg-green-200';
  };

  const getTextColor = (intensity: number): string => {
    return intensity > 60 ? 'text-white' : 'text-foreground';
  };

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            onClick={() => onClick(data)}
            className={`${getIntensityColor(data.intensity)} ${getTextColor(data.intensity)}
              border border-border/50 rounded-sm cursor-pointer transition-all hover:scale-105 hover:shadow-md
              flex items-center justify-center relative z-0 hover:z-10`}
            style={{ width: `${width}px`, height: `${height}px` }}
          />
        </TooltipTrigger>
        <TooltipContent>
          <div className="text-xs">
            <p className="font-bold">{data.day} {data.hour}:00</p>
            <p>売上: ¥{data.revenue.toLocaleString()}</p>
            <p>来店: {data.customers}名</p>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

/**
 * 動的なレスポンシブ設定
 */
const useDynamicResponsive = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [cellWidth, setCellWidth] = useState(48);
  const [cellHeight] = useState(48); // 高さは固定
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const calculateCellSize = () => {
      if (!containerRef.current) return;

      const isMobileView = window.innerWidth < 768;
      setIsMobile(isMobileView);

      const containerWidth = containerRef.current.offsetWidth;
      const labelWidth = isMobileView ? 20 : 40;
      const hoursCount = 13;
      const gapSize = isMobileView ? 2 : 4;
      const gapTotal = (hoursCount - 1) * gapSize;
      const padding = 32;

      // 横幅を動的に計算（正方形制約を解除）
      const availableWidth = containerWidth - labelWidth - gapTotal - padding;
      const optimalCellWidth = Math.floor(availableWidth / hoursCount);
      // 最小幅24pxのみ制約、最大幅制約を解除してコンテナ幅を最大活用
      const finalWidth = Math.max(24, optimalCellWidth);
      setCellWidth(finalWidth);
    };

    calculateCellSize();
    const handleResize = () => calculateCellSize();
    window.addEventListener('resize', handleResize);
    setTimeout(calculateCellSize, 100);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const config = useMemo(() => ({
    cellWidth,
    cellHeight,
    gap: isMobile ? 2 : 4,
    dayLabelWidth: isMobile ? 20 : 40
  }), [cellWidth, cellHeight, isMobile]);

  return { config, containerRef, isMobile };
};

/**
 * パフォーマンスヒートマップコンポーネント
 */
export const PerformanceHeatmap: React.FC = () => {
  const { config, containerRef, isMobile } = useDynamicResponsive();
  const [selectedCell, setSelectedCell] = useState<HeatmapData | null>(null);
  
  // 実データ取得State
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [treatments, setTreatments] = useState<TreatmentRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // データ取得
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [reservationsData, treatmentsData] = await Promise.all([
          DataService.getReservations(),
          DataService.getTreatments(),
        ]);
        setReservations(reservationsData);
        setTreatments(treatmentsData);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'データ取得エラー');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const heatmapData = useMemo(() => {
    if (!reservations || reservations.length === 0) {
      return [];
    }
    
    const data: HeatmapData[] = [];
    const days = ['月', '火', '水', '木', '金', '土', '日'];
    
    // 過去30日間のデータを使用
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    
    // 曜日×時間帯ごとの集計マップ
    const aggregationMap = new Map<string, { revenue: number; customers: number; count: number }>();
    
    // 初期化（全曜日×全時間帯）
    days.forEach((_, dayIndex) => {
      for (let hour = 9; hour <= 21; hour++) {
        const key = `${dayIndex}-${hour}`;
        aggregationMap.set(key, { revenue: 0, customers: 0, count: 0 });
      }
    });
    
    // 予約データから集計
    reservations.forEach(reservation => {
      const reservationDate = new Date(reservation.date);
      
      // 過去30日以内かつ完了した予約のみ
      if (reservationDate >= thirtyDaysAgo && reservation.status === 'completed') {
        const dayIndex = reservationDate.getDay(); // 0=日曜, 1=月曜, ..., 6=土曜
        const adjustedDayIndex = dayIndex === 0 ? 6 : dayIndex - 1; // 0=月曜, 6=日曜に調整
        
        // 開始時間からhourを抽出
        const startTime = reservation.startTime || '00:00';
        const hour = parseInt(startTime.split(':')[0], 10);
        
        // 9-21時の範囲内のみ
        if (hour >= 9 && hour <= 21) {
          const key = `${adjustedDayIndex}-${hour}`;
          const cell = aggregationMap.get(key);
          
          if (cell) {
            // 施術データから売上を取得
            const treatment = treatments.find(t => t.reservationId === reservation.id);
            const revenue = treatment?.totalAmount || 0;
            
            cell.revenue += revenue;
            cell.customers += 1;
            cell.count += 1;
          }
        }
      }
    });
    
    // 最大売上を計算（intensity計算用）
    let maxRevenue = 0;
    aggregationMap.forEach(cell => {
      if (cell.revenue > maxRevenue) {
        maxRevenue = cell.revenue;
      }
    });
    
    // ヒートマップデータ生成
    days.forEach((day, dayIndex) => {
      for (let hour = 9; hour <= 21; hour++) {
        const key = `${dayIndex}-${hour}`;
        const cell = aggregationMap.get(key);
        
        if (cell) {
          // intensityを0-100にスケーリング
          const intensity = maxRevenue > 0 ? 
            Math.round((cell.revenue / maxRevenue) * 100) : 0;
          
          data.push({
            day,
            dayIndex,
            hour,
            revenue: cell.revenue,
            intensity,
            customers: cell.customers,
          });
        } else {
          // データがない場合は0で初期化
          data.push({
            day,
            dayIndex,
            hour,
            revenue: 0,
            intensity: 0,
            customers: 0,
          });
        }
      }
    });
    
    return data;
  }, [reservations, treatments]);

  const handleCellClick = (data: HeatmapData) => {
    setSelectedCell(data);
  };

  // Loading状態
  if (loading) {
    return (
      <Card className="min-h-[350px] md:min-h-[450px]">
        <CardHeader>
          <CardTitle className="text-base font-semibold">パフォーマンスヒートマップ</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-[300px]">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            <span className="ml-3 text-muted-foreground">データを読み込み中...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Error状態
  if (error) {
    return (
      <Card className="min-h-[350px] md:min-h-[450px]">
        <CardHeader>
          <CardTitle className="text-base font-semibold">パフォーマンスヒートマップ</CardTitle>
        </CardHeader>
        <CardContent>
          <Alert variant="destructive">
            <AlertDescription>
              データの読み込みに失敗しました: {error}
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  // 空データ状態
  if (heatmapData.length === 0) {
    return (
      <Card className="min-h-[350px] md:min-h-[450px]">
        <CardHeader>
          <CardTitle className="text-base font-semibold">パフォーマンスヒートマップ</CardTitle>
        </CardHeader>
        <CardContent>
          <Alert>
            <AlertDescription>
              表示するデータがありません。予約や施術記録を追加してください。
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="min-h-[350px] md:min-h-[450px]">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Clock className="text-primary" />
            <CardTitle className="text-base font-semibold">
              パフォーマンスヒートマップ
            </CardTitle>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <Info className="w-4 h-4 text-muted-foreground" />
                </TooltipTrigger>
                <TooltipContent>
                  <p>7日間×24時間の売上パターンを可視化</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <Badge variant="outline">週間パターン分析</Badge>
        </div>
      </CardHeader>

      <CardContent>
        {isMobile && (
          <Alert className="mb-4">
            <AlertDescription className="text-xs">
              📱 モバイルディスプレイ用に最適化されています
            </AlertDescription>
          </Alert>
        )}

        <div ref={containerRef} className={`mb-6 ${isMobile ? 'overflow-auto' : ''} w-full flex flex-col`}>
          {/* 時間軸ヘッダー */}
          <div className="flex items-center mb-2">
            <div
              style={{ width: `${config.dayLabelWidth}px`, marginRight: `${config.gap}px` }}
              className="flex-shrink-0"
            />
            {Array.from({ length: 13 }, (_, i) => i + 9).map(hour => (
              <div
                key={hour}
                style={{
                  width: `${config.cellWidth}px`,
                  marginRight: hour < 21 ? `${config.gap}px` : 0
                }}
                className={`text-center flex-shrink-0 text-muted-foreground ${
                  config.cellWidth < 24 ? 'text-[0.6rem]' : 'text-xs'
                }`}
              >
                {hour}
              </div>
            ))}
          </div>

          {/* ヒートマップ本体 */}
          {['月', '火', '水', '木', '金', '土', '日'].map((day, dayIndex) => (
            <div key={day} className="flex items-center mb-1 w-full">
              <div
                style={{ 
                  width: `${config.dayLabelWidth}px`,
                  marginRight: `${config.gap}px`
                }}
                className={`text-center flex-shrink-0 ${
                  config.dayLabelWidth < 30 ? 'text-[0.7rem]' : 'text-xs'
                } ${
                  dayIndex >= 5 ? 'font-bold text-primary' : 
                  dayIndex === 0 ? 'font-bold text-destructive' : 
                  'text-muted-foreground'
                }`}
              >
                {day}
              </div>

              <div className="flex flex-1">
                {Array.from({ length: 13 }, (_, i) => {
                  const hour = i + 9;
                  const cellData = heatmapData.find(d => d.dayIndex === dayIndex && d.hour === hour);
                  return (
                    <div
                      key={`${day}-${hour}`}
                      style={{ marginRight: hour < 21 ? `${config.gap}px` : 0 }}
                      className="flex-shrink-0"
                    >
                      {cellData ? (
                        <HeatmapCell
                          data={cellData}
                          onClick={handleCellClick}
                          width={config.cellWidth}
                          height={config.cellHeight}
                        />
                      ) : (
                        <div
                          style={{
                            width: `${config.cellWidth}px`,
                            height: `${config.cellHeight}px`
                          }}
                          className="bg-muted/30 border border-border rounded-sm"
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* 売上強度凡例 */}
        <div className="mt-6 pt-4 border-t">
          <p className="text-sm font-semibold mb-3">💰 売上強度の見方</p>
          <div className={`grid ${isMobile ? 'grid-cols-3' : 'grid-cols-5'} gap-3`}>
            {[
              { color: 'bg-muted/30 border border-border', label: '売上なし', range: '¥0' },
              { color: 'bg-green-200', label: '軽度', range: '〜¥8,000' },
              { color: 'bg-lime-500', label: '普通', range: '¥8K〜¥15K' },
              { color: 'bg-green-600', label: '好調', range: '¥15K〜¥20K' },
              { color: 'bg-green-900', label: '絶好調', range: '¥20K〜' }
            ].map((item, index) => (
              <div key={index} className="text-center">
                <div className={`w-6 h-6 ${item.color} rounded mx-auto mb-1`} />
                <p className="text-xs font-semibold">{item.label}</p>
                <p className="text-[0.6rem] text-muted-foreground">{item.range}</p>
              </div>
            ))}
          </div>
        </div>

        {/* 選択されたセルの詳細 */}
        {selectedCell && (
          <div className="mt-4 p-3 bg-primary text-primary-foreground rounded-lg flex items-center gap-2">
            <TrendingUp className="w-4 h-4" />
            <p className="text-sm">
              <strong>{selectedCell.day}曜日 {selectedCell.hour}:00</strong> -
              売上: ¥{selectedCell.revenue.toLocaleString()} |
              来店: {selectedCell.customers}名
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
